/* eslint-disable react-hooks/set-state-in-effect -- URL ⇄ app state is an
   external-signal sync: the READ effect reconciles the open channel to the
   address bar on load, the WRITE effect reconciles the address bar to state. */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";
import { parseAppPath, buildAppPath } from "@/lib/app-route";
import {
  generateSlugFromName,
  getWorkspaceSlugFromLocation,
} from "@/lib/workspace-validation";

interface UseAppUrlSyncArgs {
  user: { id: string } | null | undefined;
  selectedWorkspace: string | null;
  /** Slug of the selected workspace (for the `/<slug>` path segment). */
  workspaceSlug: string | null;
  selectedChannel: string | null;
  setSelectedChannel: (id: string | null) => void;
}

interface ChannelRow {
  id: string;
  name: string;
  type: string | null;
}

/** Lookup maps for channel ⇄ name resolution — real `channel` rows only (DMs
 *  aren't deep-linkable). Name match is case-insensitive; first row wins. */
export function channelMapsFromRows(rows: readonly ChannelRow[]) {
  const idByName = new Map<string, string>();
  const nameById = new Map<string, string>();
  for (const c of rows) {
    if (c.type !== "channel") continue;
    nameById.set(c.id, c.name);
    const key = c.name.toLowerCase();
    if (!idByName.has(key)) idByName.set(key, c.id);
  }
  return { idByName, nameById };
}

/**
 * The path that reflects the current workspace + open channel:
 *  - no workspace        → "/" (home)
 *  - workspace, no channel → "/<slug>"
 *  - workspace + channel   → "/<slug>/c/<name>"
 *  - a DM / cross-workspace channel → "/<slug>" (no deep link for DMs)
 *  - channels not loaded yet (map empty) → `null`, i.e. "leave the URL alone"
 *    so an initial `/<slug>/c/<name>` deep link isn't clobbered before it resolves.
 */
export function urlPathForRoute(
  workspaceSlug: string | null,
  selectedChannel: string | null,
  nameById: Map<string, string>,
): string | null {
  if (!workspaceSlug) return "/";
  if (!selectedChannel) return buildAppPath(workspaceSlug);
  const name = nameById.get(selectedChannel);
  if (name) return buildAppPath(workspaceSlug, name);
  return nameById.size === 0 ? null : buildAppPath(workspaceSlug);
}

export function shouldSuppressUrlWriteForUnknownWorkspace({
  deepLinkSlug,
  urlWorkspaceId,
  workspaceStatusType,
}: {
  deepLinkSlug: string | null;
  urlWorkspaceId: string | null;
  workspaceStatusType: string;
}): boolean {
  return Boolean(
    deepLinkSlug && !urlWorkspaceId && workspaceStatusType === "complete",
  );
}

/** Capture the deep link once, at mount. A bare `/c/<name>` on the canonical
 *  app host (no workspace context) is NOT a deep link — only honor a channel
 *  when the URL carries a workspace slug or we're on a legacy subdomain. */
function readDeepLink(): { slug: string | null; channel: string | null } {
  const route = parseAppPath();
  const onSubdomain = !!getWorkspaceSlugFromLocation(window.location);
  return {
    slug: route.workspaceSlug,
    channel: route.workspaceSlug || onSubdomain ? route.channelName : null,
  };
}

/**
 * Reflects the open channel into the `/<slug>/c/<name>` URL and applies a
 * channel deep link on load. The WORKSPACE half of the path is read by
 * `useWorkspaceScope` (pinning) and written here; this hook owns the channel
 * half + the single `replaceState` writer for the whole path.
 *
 *  - READ once on load: a `/<slug>/c/<name>` (or legacy `/c/<name>`) URL selects
 *    that channel — but ONLY against the URL's OWN workspace, after it pins.
 *    Never resolves the channel name against a stale/localStorage workspace
 *    (channel names aren't globally unique). Gives up if the URL workspace
 *    isn't one the user can see.
 *  - WRITE: thereafter reflect (workspace, channel) into the path, never
 *    clobbering a not-yet-applied deep link.
 */
export function useAppUrlSync({
  user,
  selectedWorkspace,
  workspaceSlug,
  selectedChannel,
  setSelectedChannel,
}: UseAppUrlSyncArgs) {
  // Captured once at mount (stable) — readable in render/memo without the
  // "ref read during render" lint, unlike a useRef.
  const [deepLink] = useState(readDeepLink);
  const [applied, setApplied] = useState(false);

  // Resolve the URL's workspace to an id from the user's own list, matching
  // either the stored slug or the name-derived slug (same as scope pinning).
  const [wsList, wsStatus] = useQuery(
    user ? queries.workspaces.byUser() : null,
  );
  const urlWorkspaceId = useMemo(() => {
    const n = deepLink.slug;
    if (!n) return null;
    const w = (wsList ?? []).find(
      (x) => x.slug === n || generateSlugFromName(x.name ?? "") === n,
    );
    return w?.id ?? null;
  }, [wsList, deepLink]);

  const [rows] = useQuery(
    user && selectedWorkspace
      ? queries.channels.byWorkspace(selectedWorkspace)
      : null,
  );
  const { idByName, nameById } = useMemo(
    () => channelMapsFromRows(rows ?? []),
    [rows],
  );

  // READ — apply the deep-link channel once the URL's workspace is the active
  // scope and its channels have loaded.
  useEffect(() => {
    if (applied) return;
    const { slug, channel } = deepLink;
    if (slug) {
      // Wait until the workspace list can resolve the URL slug.
      if (!urlWorkspaceId && wsStatus.type !== "complete") return;
      if (!urlWorkspaceId) {
        setApplied(true); // not a workspace the user can see → give up
        return;
      }
      if (selectedWorkspace !== urlWorkspaceId) return; // wait for the pin
    }
    if (channel && idByName.size === 0) return; // wait for channels
    if (channel) {
      const id = idByName.get(channel.toLowerCase());
      if (id) setSelectedChannel(id);
    }
    setApplied(true);
  }, [
    applied,
    deepLink,
    urlWorkspaceId,
    wsStatus.type,
    selectedWorkspace,
    idByName,
    setSelectedChannel,
  ]);

  // WRITE — reflect (workspace, channel) into the path once the deep link is
  // applied. Don't clobber `/acme/c/db` to `/` while the slug is still
  // resolving (workspace selected but not yet found in the synced list).
  useEffect(() => {
    if (!applied) return;
    if (
      shouldSuppressUrlWriteForUnknownWorkspace({
        deepLinkSlug: deepLink.slug,
        urlWorkspaceId,
        workspaceStatusType: wsStatus.type,
      })
    ) {
      return;
    }
    if (selectedWorkspace && !workspaceSlug) return;
    const path = urlPathForRoute(workspaceSlug, selectedChannel, nameById);
    if (path && path !== window.location.pathname) {
      window.history.replaceState(
        {},
        "",
        `${path}${window.location.search}${window.location.hash}`,
      );
    }
  }, [
    applied,
    deepLink.slug,
    urlWorkspaceId,
    wsStatus.type,
    selectedWorkspace,
    workspaceSlug,
    selectedChannel,
    nameById,
  ]);
}
